import { ref } from 'vue';
import { useApi } from './useApi';
import { useAppState } from './useAppState';
import { getHttpInstance, getApiConfig } from './useApi';
// Set true by pages that mutate saving goals so the next read on another page
// knows to SWR+poll until the server's /Cached payload reflects the write.
// Mirrors `pendingBankingRevalidate` in useBanking.ts.
export const pendingGoalsRevalidate = ref(false);
// Dedupe in-flight pollUntilChanged cycles so concurrent callers attach to
// one poll loop instead of multiplying network load.
let savingGoalsPolling = false;
const savingGoalsWaiters = [];
// Stable change key for the saving-goals SWR poll. Built from identity +
// fields that the create/delete flows actually move, so volatile fields
// don't make pollUntilChanged bail out before the server has caught up.
export function savingGoalsChangeKey(goals) {
    const list = Array.isArray(goals) ? goals : [];
    const parts = [];
    for (const g of list) {
        parts.push(`${g.id}:${g.currentBalance ?? ''}:${g.targetAmount ?? ''}:${g.archived ? 1 : 0}`);
    }
    return parts.join('|');
}
export function useGoals() {
    const api = useApi();
    const state = useAppState();
    function getSavingGoals(onSuccess = null, staleWhileRevalidate = false, pollUntilChanged = false) {
        const url = "/Customers/00000000-0000-0000-0000-000000000000/SavingsGoals";
        // Attach to an existing poll cycle instead of starting a second one.
        if (pollUntilChanged && savingGoalsPolling) {
            if (onSuccess) {
                if (state.savingGoals)
                    onSuccess(state.savingGoals, /* isStale */ true);
                savingGoalsWaiters.push((isStale) => onSuccess(state.savingGoals, isStale));
            }
            return;
        }
        if (pollUntilChanged)
            savingGoalsPolling = true;
        api.GET(url, (result, isStale) => {
            state.savingGoals = result;
            if (onSuccess)
                onSuccess(result, isStale);
            if (!isStale && pollUntilChanged) {
                savingGoalsPolling = false;
                const waiters = savingGoalsWaiters.splice(0);
                waiters.forEach(cb => cb(/* isStale */ false));
            }
        }, /* useCache: */ true, /* forceSetCache */ false, /* ttlMins */ undefined, /* retrying */ false, staleWhileRevalidate, pollUntilChanged, savingGoalsChangeKey);
    }
    function clearSavingGoals() {
        const config = getApiConfig();
        localStorage.removeItem(config.api + (config.useCachedEndpoints ? '/Cached' : '') + '/Customers/00000000-0000-0000-0000-000000000000/SavingsGoals');
    }
    function getSavingGoal(id, onSuccess = null) {
        api.GET("/Customers/00000000-0000-0000-0000-000000000000/SavingsGoals/" + id, (result) => {
            if (onSuccess) {
                onSuccess(result);
            }
        }, /* useCache: */ true);
    }
    function deleteSavingGoal(id, onSuccess = null) {
        getHttpInstance().delete(getApiConfig().api + "/Customers/00000000-0000-0000-0000-000000000000/SavingsGoals/" + id)
            .then((response) => {
            pendingGoalsRevalidate.value = true;
            if (onSuccess) {
                onSuccess(response.data);
            }
        })
            .catch((result) => {
            if (result.response.status == 401) {
                api.refreshLogin(() => {
                    deleteSavingGoal(id, onSuccess);
                });
            }
        });
    }
    function quoteSavingGoal(goal, onSuccess = null) {
        getHttpInstance().post(getApiConfig().api + "/Customers/00000000-0000-0000-0000-000000000000/SavingsGoals/BankAccounts/" + goal.bankAccountID + "/Quotes", goal)
            .then((response) => {
            if (onSuccess) {
                onSuccess(response.data);
            }
        })
            .catch((result) => {
            if (result.response.status == 401) {
                api.refreshLogin(() => {
                    quoteSavingGoal(goal, onSuccess);
                });
            }
        });
    }
    function createSavingGoal(goal, onSuccess = null, onError = null) {
        getHttpInstance().post(getApiConfig().api + "/Customers/00000000-0000-0000-0000-000000000000/SavingsGoals/", goal)
            .then((response) => {
            pendingGoalsRevalidate.value = true;
            if (onSuccess) {
                onSuccess(response.data);
            }
        })
            .catch((result) => {
            if (result.response?.status == 401) {
                api.refreshLogin(() => {
                    createSavingGoal(goal, onSuccess, onError);
                });
                return;
            }
            if (onError)
                onError('Sorry, we couldn\'t create your goal. Please check the details and try again.');
        });
    }
    return {
        getSavingGoals,
        clearSavingGoals,
        getSavingGoal,
        deleteSavingGoal,
        quoteSavingGoal,
        createSavingGoal,
    };
}
